import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'react-bootstrap';

// The two values the server stores in `outcome`
// (server/src/api/listing/listing.constants.js). Both close the listing, so
// the help text is what tells a member which one their situation is.
const OUTCOMES = [
    {
        value: 'transferred',
        label: 'Transferred',
        help:
            'The item changed hands and is now with the department that ' +
            'took it.',
    },
    {
        value: 'withdrawn',
        label: 'Withdrawn',
        help: 'The exchange did not happen and the item is no longer on offer.',
    },
];

/**
 * Close-out, with the outcome the listing is allowed to carry.
 *
 * A listing with no accepted request is transferred or nothing: withdrawn
 * says an arranged exchange fell through, and the server refuses it here
 * with NO_EXCHANGE_TO_WITHDRAW. So the choice is not offered on one, which
 * leaves the member the control that does say the item is not going
 * anywhere, Remove, and unlike this one that can be undone.
 */
function CompleteExchangeModal({
    show,
    listingTitle,
    claimed = true,
    submitting = false,
    onCancel,
    onConfirm,
}) {
    const [outcome, setOutcome] = useState(OUTCOMES[0].value);

    // Each close-out is its own decision, so reopening starts from the
    // default rather than from whatever was picked and abandoned last time.
    useEffect(() => {
        if (show) {
            setOutcome(OUTCOMES[0].value);
        }
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={onCancel}
            aria-labelledby="complete-exchange-title"
        >
            <Modal.Header closeButton>
                <Modal.Title
                    as="h2"
                    className="h5"
                    id="complete-exchange-title"
                >
                    Close out {listingTitle}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!claimed && (
                    <p>
                        {OUTCOMES[0].help} Use Remove instead if it is staying
                        where it is.
                    </p>
                )}
                {claimed && (
                    <fieldset>
                        <legend className="h6">What happened?</legend>
                        {OUTCOMES.map((option) => (
                            <div key={option.value} className="mb-3">
                                <Form.Check
                                    type="radio"
                                    name="complete-outcome"
                                    id={`complete-outcome-${option.value}`}
                                    label={option.label}
                                    checked={outcome === option.value}
                                    aria-describedby={`complete-outcome-${option.value}-help`}
                                    onChange={() => setOutcome(option.value)}
                                />
                                <Form.Text
                                    id={`complete-outcome-${option.value}-help`}
                                    className="ms-4 d-block"
                                >
                                    {option.help}
                                </Form.Text>
                            </div>
                        ))}
                    </fieldset>
                )}
                <p className="mb-0 text-muted">
                    This closes the listing. It cannot be reopened.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel}>
                    Not yet
                </Button>
                <Button
                    disabled={submitting}
                    onClick={() => onConfirm(outcome)}
                >
                    Complete exchange
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

CompleteExchangeModal.propTypes = {
    show: PropTypes.bool,
    listingTitle: PropTypes.string,
    claimed: PropTypes.bool,
    submitting: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
};

export default CompleteExchangeModal;
